import { apiClient } from './apiClient'
import { withApiErrorHandling } from '../utils/apiErrorWrapper'
import { Tenant, Manager, CodeAvailabilityResponse, WeatherStatusDTO, WeatherStatus } from '../types/dtos'

export interface WeatherStatusRequest {
  status: WeatherStatus
  reason?: string
  estimatedResumeAt?: string
}

const tenantErrors = {
  forbidden: 'You do not have permission to perform this action. SUPERUSER access required.',
  notFound: 'Tenant not found or has been deleted',
  conflict: 'Tenant code already exists. Please choose a different code.'
}

class TenantService {
  getActiveTenants = withApiErrorHandling(
    async (): Promise<Tenant[]> => {
      const response = await apiClient.get<Tenant[] | { content: Tenant[] }>('/tenants')
      const data = response.data
      return Array.isArray(data) ? data : (data.content || [])
    },
    {
      logContext: 'Failed to fetch active tenants',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to load tenants' }
    }
  )

  getTenantById = withApiErrorHandling(
    async (tenantId: string): Promise<Tenant> => {
      const response = await apiClient.get<Tenant>(`/tenants/${tenantId}`)
      return response.data
    },
    {
      logContext: 'Failed to fetch tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to load tenant details' }
    }
  )

  isCodeAvailable = withApiErrorHandling(
    async (code: string): Promise<CodeAvailabilityResponse> => {
      const response = await apiClient.get<CodeAvailabilityResponse>(`/tenants/code-available/${code}`)
      return response.data
    },
    {
      logContext: 'Failed to check code availability',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to check code availability' }
    }
  )

  createTenant = withApiErrorHandling(
    async (tenantData: Record<string, unknown>): Promise<Tenant> => {
      const response = await apiClient.post<Tenant>('/tenants', tenantData)
      return response.data
    },
    {
      logContext: 'Failed to create tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to create tenant' }
    }
  )

  updateTenant = withApiErrorHandling(
    async (tenantId: string, tenantData: Record<string, unknown>): Promise<Tenant> => {
      const response = await apiClient.put<Tenant>(`/tenants/${tenantId}`, tenantData)
      return response.data
    },
    {
      logContext: 'Failed to update tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to update tenant' }
    }
  )

  deleteTenant = withApiErrorHandling(
    async (tenantId: string): Promise<boolean> => {
      await apiClient.delete(`/tenants/${tenantId}`)
      return true
    },
    {
      logContext: 'Failed to delete tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to delete tenant' }
    }
  )

  getTenantManagers = withApiErrorHandling(
    async (tenantId: string): Promise<Manager[]> => {
      const response = await apiClient.get<Manager[]>(`/tenants/${tenantId}/managers`)
      return response.data
    },
    {
      logContext: 'Failed to fetch managers for tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to load tenant managers' }
    }
  )

  assignManagerToTenant = withApiErrorHandling(
    async (tenantId: string, userId: string): Promise<boolean> => {
      await apiClient.post(`/tenants/${tenantId}/managers/${userId}`)
      return true
    },
    {
      logContext: 'Failed to assign manager to tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to assign manager' }
    }
  )

  removeManagerFromTenant = withApiErrorHandling(
    async (tenantId: string, userId: string): Promise<boolean> => {
      await apiClient.delete(`/tenants/${tenantId}/managers/${userId}`)
      return true
    },
    {
      logContext: 'Failed to remove manager from tenant',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to remove manager' }
    }
  )

  getAvailableManagers = withApiErrorHandling(
    async (): Promise<Manager[]> => {
      const response = await apiClient.get<Manager[]>('/tenants/available-managers')
      return response.data
    },
    {
      logContext: 'Failed to fetch available managers',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to load available managers' }
    }
  )

  getWeatherStatus = withApiErrorHandling(
    async (tenantId: string): Promise<WeatherStatusDTO> => {
      const response = await apiClient.get<WeatherStatusDTO>(`/tenants/${tenantId}/weather-status`)
      return response.data
    },
    {
      logContext: 'Failed to fetch weather status',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to load weather status' }
    }
  )

  setWeatherStatus = withApiErrorHandling(
    async (tenantId: string, body: WeatherStatusRequest): Promise<WeatherStatusDTO> => {
      const response = await apiClient.put<WeatherStatusDTO>(`/tenants/${tenantId}/weather-status`, body)
      return response.data
    },
    {
      logContext: 'Failed to update weather status',
      errorMessages: { ...tenantErrors, defaultMessage: 'Failed to update weather status' }
    }
  )

  // Public, unauthenticated read by club code — used by the shared weather banner's live poll on
  // the player/public surfaces. Left unwrapped so a transient poll failure doesn't spam error logs;
  // callers swallow the error and keep the last known status.
  getPublicWeatherStatus = async (tenantCode: string): Promise<WeatherStatusDTO> => {
    const response = await apiClient.get<WeatherStatusDTO>(`/public/tenants/${tenantCode}/weather-status`)
    return response.data
  }
}

export const tenantService = new TenantService()
